import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { UntypedFormControl } from "@angular/forms";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
} from "rxjs/operators";
import { Observable, Subscription } from "rxjs";
import {
  defaultEmployeeListPager,
  EmployeeListItem,
  EmployeeListSearchParams,
  EmployeesState,
  ViewType,
} from "../employees.interface";
import { PaginatedData } from "../../shared/model/table.interface";
import {
  ChangeExcludedFromSignaturesAction,
  ClearEmployeesParamsAction,
  ExcludeAllEmployeesAction,
  RemoveSignatureTemplateAction,
  SetEmployeesParamsAction,
} from "../employees.actions";
import { getEmployeesState, isLoadingSelector } from "../employees.reducer";
import { Signature } from "../../signatures/signatures.interface";
import { GetSignaturesAction } from "../../signatures/signatures.actions";
import { getSignaturesSelector } from "../../signatures/signatures.reducer";
import { EmployeesService } from "../employees.service";
import { AuthService } from "../../auth/auth.service";
import { UserService } from "../../users/users.service";
import { saveAs } from "file-saver";
import { getCurrentAccount } from "src/app/accounts/accounts.reducer";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationDialogComponent } from "../../shared/confirmation-dialog/confirmation-dialog.component";
import { EmployeeRequestDialogComponent } from "../employee-request-dialog/employee-request-dialog.component";
import { EmployeeEditDialogComponent } from "../employee-edit-dialog/employee-edit-dialog.component";
import { EmployeeManagerComponent } from "../employee-manager/employee-manager.component";
import { Unit, User } from "../../users/users.interface";
import { Branch } from "../../branch-settings/branch-settings.interface";
import { BranchSettingsService } from "../../branch-settings/branch-settings.service";

@Component({
  selector: "app-employee-list",
  templateUrl: "./employee-list.component.html",
  styleUrls: ["./employee-list.component.scss"],
  standalone: false,
})
export class EmployeeListComponent implements OnInit, OnDestroy {
  public displayedColumns = [
    "fullName",
    "email",
    "type",
    "createdAt",
    "modifiedAt",
    "signatureInstallationAt",
    "templateId",
    "signatureAccepted",
    "excludedFromSignatures",
    "id",
  ];
  public dataSource: MatTableDataSource<EmployeeListItem>;
  public pageSizeOptions = [10, 20, 100];
  public total = 0;
  public pageIndex = defaultEmployeeListPager.page;
  public pageSize = defaultEmployeeListPager.limit;
  public filters: EmployeeListSearchParams;
  public phraseControl = new UntypedFormControl("");
  private phraseControlSubscription: Subscription;
  private currentDomain: string | null = null;
  public groupEmails = {
    data: null as string[] | null,
    loading: false,
  };
  public units = {
    data: null as Unit[] | null,
    loading: false,
  };
  public branches = {
    data: null as Branch[] | null,
    loading: false,
  };
  public allSignatures: Signature[] = [];
  readonly identityLabel = (value: string) => value;
  readonly signatureLabel = (signature: Signature) => signature.name || "";
  readonly signatureValue = (signature: Signature) => signature.templateId;
  readonly unitLabel = (unit: Unit) => unit.name || "";
  readonly unitValue = (unit: Unit) => unit.orgUnitPath;
  readonly unitSearchLabel = (unit: Unit) =>
    `${unit.name || ""} ${unit.orgUnitPath || ""}`;
  readonly branchLabel = (branch: Branch) => branch.name || "";
  readonly branchValue = (branch: Branch) => branch.branchId;
  public groupMembersMap: Map<string, string[]> = new Map();
  @ViewChild(MatSort) sort: MatSort;

  public account$: Observable<string>;
  public signatures$: Observable<Signature[]>;
  public signatureNamePerId$: Observable<Map<string, string>>;
  public signatureInternalNamePerId$: Observable<Map<string, string>>;
  public isLoading$: Observable<boolean>;
  public defaultSignatureFilter?: string;
  public appUser: User | undefined;
  public isAllExcluded = false;
  public isSomeExcluded = false;
  public canExcludeAnyEmployee = false;
  public processingEmployees = new Set<string>();
  public isExcludingAll = false;
  public removingSignatureTemplates = new Set<string>();
  signatureNameMap = new Map<string, string>();
  private employeeDialogOpen = false;
  private employeeQuerySubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private employeesService: EmployeesService,
    public authService: AuthService,
    private userService: UserService,
    private branchSettingsService: BranchSettingsService,
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.appUser = this.authService.appUser;
    this.clearFilters();
    this.store.dispatch(new GetSignaturesAction(true));
    this.dataSource = new MatTableDataSource<EmployeeListItem>([]);
    this.employeeQuerySubscription = this.route.queryParamMap
      .pipe(
        map((params) => (params.get("employeeId") || "").trim()),
        distinctUntilChanged(),
        filter((employeeId) => employeeId.length > 0),
      )
      .subscribe((employeeId) => {
        this.openEmployeeEditDialog(employeeId);
      });
    this.store.select(getCurrentAccount).subscribe((domain) => {
      this.currentDomain = domain;
    });

    if (!this.appUser) {
      void this.authService.fetchCurrent().then(() => {
        this.appUser = this.authService.appUser;
      });
    }

    this.store.select(getEmployeesState).subscribe((state: EmployeesState) => {
      this.updateCheckboxState(state.employees.data);
      const { employees, params } = state;
      this.filters = { ...params };
      this.updateDataSource(employees);
      this.updatePaginator(employees, params);

      if (!this.filters.viewType) {
        this.filters.viewType = "EMPLOYEES";
      }

      this.processingEmployees.forEach((employeeId) => {
        const updatedEmployee = employees.data.find(
          (emp) => emp.employeeId === employeeId,
        );
        if (updatedEmployee) {
          this.processingEmployees.delete(employeeId);
        }
      });

      this.isExcludingAll = false;
      this.removingSignatureTemplates.clear();
    });

    this.employeesService.refreshEmployees$.subscribe(() => {
      this.store.dispatch(new SetEmployeesParamsAction(this.filters));
    });

    this.defaultSignatureFilter = (this.route.snapshot.queryParams.templateId ||
      this.route.snapshot.queryParams.signatureTemplateId) as string;
    const isAliasFilter = this.route.snapshot.queryParams.aliases === "true";

    this.filters.viewType = isAliasFilter
      ? "ALIASES"
      : this.filters.viewType || "EMPLOYEES";

    if (this.defaultSignatureFilter) {
      this.filters.templateId = this.defaultSignatureFilter;
      this.store.dispatch(new SetEmployeesParamsAction(this.filters));
    }

    this.changeDataOnSearchPhraseChanges();
    this.signatures$ = this.store.select(getSignaturesSelector);
    this.isLoading$ = this.store.select(isLoadingSelector);

    this.signatureNamePerId$ = this.store.select(getSignaturesSelector).pipe(
      map(
        (signatures) =>
          new Map(signatures?.map((s) => [s.templateId ?? "", s.name]) ?? []),
      ),
      startWith(new Map()),
    );
    this.store.select(getSignaturesSelector).subscribe((signatures) => {
      this.allSignatures = signatures ?? [];
      this.signatureNameMap = new Map(
        signatures?.map((s) => [s.templateId ?? "", s.name]) ?? [],
      );
    });
    this.signatureInternalNamePerId$ = this.store
      .select(getSignaturesSelector)
      .pipe(
        map(
          (signatures) =>
            new Map(signatures?.map((s) => [s.templateId ?? "", s.name]) ?? []),
        ),
        startWith(new Map()),
      );
  }

  private updateCheckboxState(employees: EmployeeListItem[]): void {
    const excludedCount = employees.filter(
      (emp) => emp.excludedFromSignatures,
    ).length;
    this.isAllExcluded = excludedCount === employees.length;
    this.isSomeExcluded = excludedCount > 0 && excludedCount < employees.length;

    this.canExcludeAnyEmployee = employees.some(
      (emp) => emp.canBeExcludedFromSignatures && !emp.isAlias,
    );
  }

  toggleExcludeAll(exclude: boolean): void {
    this.isExcludingAll = true;
    this.store.dispatch(
      new ExcludeAllEmployeesAction({
        filters: this.filters,
        exclude,
      }),
    );
  }

  changePage = (event: PageEvent) => {
    this.filters.pager = {
      limit: event.pageSize,
      page: event.pageIndex,
    };
    this.store.dispatch(new SetEmployeesParamsAction(this.filters));
  };

  sortChange = ({
    active,
    direction,
  }: {
    active: string;
    direction: string;
  }) => {
    this.filters.order = direction.toUpperCase() || undefined;
    this.filters.sortBy = (active === "id" ? "employeeId" : active) as
      | "fullName"
      | "email"
      | "createdAt"
      | "modifiedAt"
      | "signatureInstallationAt"
      | "employeeId";
    this.resetPager();
    this.store.dispatch(new SetEmployeesParamsAction(this.filters));
  };

  changeExcludedFromSignatures = (
    checked: boolean,
    employee: EmployeeListItem,
  ) => {
    const employeeId = employee.employeeId;
    if (!employeeId) {
      return;
    }
    this.processingEmployees.add(employeeId);
    this.store.dispatch(
      new ChangeExcludedFromSignaturesAction({
        employeeId,
        excludedFromSignatures: checked,
      }),
    );
  };

  private resetPager = () =>
    (this.filters.pager = { ...this.filters.pager, page: 0 });

  private updateDataSource = (employeeList: PaginatedData<EmployeeListItem>) =>
    (this.dataSource.data = employeeList.data);

  private updatePaginator = (
    employeeList: PaginatedData<EmployeeListItem>,
    params: EmployeeListSearchParams,
  ) => {
    this.total = employeeList.total;
    if (params?.pager) {
      this.pageIndex = params.pager.page;
      this.pageSize = params.pager.limit;
    }
  };

  public loadGroupEmails(): void {
    if (this.groupEmails.data !== null || this.groupEmails.loading) {
      return;
    }
    this.groupEmails.loading = true;
    this.userService.groups().subscribe((groups) => {
      this.groupEmails.data = groups?.map((group) => group.email) ?? [];
      this.groupEmails.loading = false;
    });
  }

  public loadUnits(): void {
    if (this.units.data !== null || this.units.loading) {
      return;
    }
    this.units.loading = true;
    this.userService.units().subscribe({
      next: (list) => {
        this.units.data = list ?? [];
        this.units.loading = false;
      },
      error: () => {
        this.units.loading = false;
      },
    });
  }

  public loadBranches(): void {
    if (this.branches.data !== null || this.branches.loading) {
      return;
    }
    this.branches.loading = true;
    this.branchSettingsService.fetchBranches().subscribe({
      next: (list) => {
        this.branches.data = list ?? [];
        this.branches.loading = false;
      },
      error: () => {
        this.branches.loading = false;
      },
    });
  }

  loadFilterData(): void {
    this.loadGroupEmails();
    this.loadUnits();
    this.loadBranches();
  }

  getActiveFilters(): Array<{ key: string; label: string }> {
    const items: Array<{ key: string; label: string }> = [];
    if (this.filters.templateId) {
      const name =
        this.signatureNameMap.get(this.filters.templateId) ??
        this.filters.templateId;
      items.push({
        key: "templateId",
        label: `${this.translate.instant("employee.filterBySignature")}: ${name}`,
      });
    }
    if (this.filters.signatureInstalled) {
      const label = this.translate.instant(
        `employee.filterByHasSignature.${this.filters.signatureInstalled}`,
      ) as string;
      items.push({ key: "signatureInstalled", label });
    }
    if (this.filters.excludedFromSignatures) {
      const label = this.translate.instant(
        `employee.filterByExcluded.${this.filters.excludedFromSignatures}`,
      ) as string;
      items.push({ key: "excludedFromSignatures", label });
    }
    if (this.filters.groupEmail) {
      items.push({
        key: "groupEmail",
        label: `${this.translate.instant("employee.filterByGroup.label")}: ${this.filters.groupEmail}`,
      });
    }
    if (this.filters.orgUnitPath && this.units.data) {
      const unit = this.units.data.find(
        (u) => u.orgUnitPath === this.filters.orgUnitPath,
      );
      const name = unit?.name ?? this.filters.orgUnitPath;
      items.push({
        key: "orgUnitPath",
        label: `${this.translate.instant("employee.filterByUnit.label")}: ${name}`,
      });
    }
    if (this.filters.branchId && this.branches.data) {
      const branch = this.branches.data.find(
        (b) => b.branchId === this.filters.branchId,
      );
      const name = branch?.name ?? this.filters.branchId;
      items.push({
        key: "branchId",
        label: `${this.translate.instant("employee.filterByBranch.label")}: ${name}`,
      });
    }
    return items;
  }

  removeFilter(key: string): void {
    switch (key) {
      case "templateId":
        this.filterBySignature(undefined);
        break;
      case "signatureInstalled":
      case "excludedFromSignatures":
      case "groupEmail":
      case "orgUnitPath":
      case "branchId":
        this.addFilter(key, undefined);
        break;
      default:
        break;
    }
  }

  get activeFiltersCount(): number {
    return this.getActiveFilters().length;
  }

  private changeDataOnSearchPhraseChanges = () => {
    this.phraseControlSubscription = this.phraseControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((phrase) => this.addFilter("phrase", phrase as string));
  };

  public addFilter = <T extends keyof EmployeeListSearchParams>(
    type: T,
    value: EmployeeListSearchParams[T],
  ) => {
    this.filters[type] = value;
    this.resetPager();
    this.store.dispatch(new SetEmployeesParamsAction(this.filters));
  };

  filterBySignature(selectedSignature?: string) {
    this.filters.templateId = selectedSignature;
    this.filters.internalTemplateId = selectedSignature;
    this.store.dispatch(new SetEmployeesParamsAction(this.filters));
  }

  clearFilters(): void {
    this.store.dispatch(new ClearEmployeesParamsAction());
  }

  toggleView(viewType: ViewType) {
    this.filters.viewType = viewType;
    this.addFilter("viewType", viewType);
  }

  getViewTypeText(viewType: ViewType): string {
    switch (viewType) {
      case "EMPLOYEES":
        return this.translate.instant(
          "employee.viewSelection.viewEmployees",
        ) as string;
      case "ALIASES":
        return this.translate.instant(
          "employee.viewSelection.viewAliases",
        ) as string;
      case "BOTH":
        return this.translate.instant(
          "employee.viewSelection.viewBoth",
        ) as string;
      default:
        return this.translate.instant(
          "employee.viewSelection.viewEmployees",
        ) as string;
    }
  }

  removeSignatureKey(element: EmployeeListItem): string {
    if (element.isAlias) {
      return element.email || "";
    }
    return element.employeeId;
  }

  removeSignatureTemplate(element: EmployeeListItem): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant(
            "employee.removeSignatureTemplateConfirmation",
          ) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.removingSignatureTemplates.add(this.removeSignatureKey(element));
          this.store.dispatch(
            new RemoveSignatureTemplateAction({
              employeeId: element.isAlias ? undefined : element.employeeId,
              email: element.isAlias ? element.email : undefined,
              isAlias: element.isAlias,
            }),
          );
        }
      });
  }

  removeInternalSignatureTemplate(element: EmployeeListItem): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant(
            "employee.removeInternalSignatureTemplateConfirmation",
          ) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.removingSignatureTemplates.add(this.removeSignatureKey(element));
          this.store.dispatch(
            new RemoveSignatureTemplateAction({
              employeeId: element.isAlias ? undefined : element.employeeId,
              email: element.isAlias ? element.email : undefined,
              isAlias: element.isAlias,
              internal: true,
            }),
          );
        }
      });
  }

  downloadAllSignatures() {
    const fileName = `${this.currentDomain || "gSignature"}.zip`;

    this.employeesService.downloadAllSignatures().subscribe(
      (blob) => {
        saveAs(blob, fileName);
      },
      (error) => {
        console.error("Failed to download signatures", error);
      },
    );
  }

  openEmployeeRequestDialog() {
    this.dialog.open(EmployeeRequestDialogComponent, {
      width: "92vw",
      maxWidth: "1280px",
      height: "90vh",
      maxHeight: "900px",
      disableClose: false,
      autoFocus: false,
      panelClass: "app-dialog-panel",
    });
  }

  employeeDetailLink(row: EmployeeListItem): string[] | null {
    return row.isAlias ? null : ["/employees"];
  }

  employeeDetailQueryParams(row: EmployeeListItem): Params | null {
    return row.isAlias ? null : { employeeId: row.employeeId };
  }

  onRowClick(row: EmployeeListItem): void {
    if (row.isAlias) {
      return;
    }
    this.openEmployeeEditDialog(row.employeeId);
  }

  openEmployeeEditDialog(employeeId: string) {
    const id = (employeeId || "").trim();
    if (!id || this.employeeDialogOpen) {
      return;
    }
    this.employeeDialogOpen = true;
    const dialogRef = this.dialog.open(EmployeeEditDialogComponent, {
      data: { employeeId: id },
      disableClose: true,
      panelClass: "app-dialog-panel",
    });

    dialogRef
      .afterClosed()
      .subscribe((result: { success: boolean; employee: EmployeeListItem }) => {
        this.employeeDialogOpen = false;
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { employeeId: null },
          queryParamsHandling: "merge",
          replaceUrl: true,
        });
        if (result && result.success) {
          const employeeIndex = this.dataSource.data.findIndex(
            (emp) => emp.employeeId === id,
          );
          if (employeeIndex !== -1) {
            this.dataSource.data[employeeIndex] = {
              ...this.dataSource.data[employeeIndex],
              ...result.employee,
            };
            this.dataSource._updateChangeSubscription();
          }
        }
      });
  }

  openEmployeeManager() {
    this.dialog.open(EmployeeManagerComponent, {
      width: "92vw",
      maxWidth: "1280px",
      height: "90vh",
      maxHeight: "900px",
      panelClass: "app-dialog-panel",
      disableClose: false,
      autoFocus: false,
    });
  }

  ngOnDestroy(): void {
    this.employeeQuerySubscription?.unsubscribe();
    this.phraseControlSubscription?.unsubscribe();
  }
}
